import React from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { AutoFormFieldProps } from "@autoform/react";

import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { hasCityCentre } from "@/pages/events/schemas/event.venue-tba";

interface CityCenter {
  geolocation: { latitude: number; longitude: number } | null;
  timezone: string | null;
}

function readCityCenter(geodata: unknown): CityCenter {
  const g = (geodata ?? {}) as {
    geolocation?: { latitude?: unknown; longitude?: unknown } | null;
    timezone?: string | null;
  };
  const geo = g.geolocation;
  return {
    geolocation: hasCityCentre(geo)
      ? {
          latitude: geo!.latitude as number,
          longitude: geo!.longitude as number,
        }
      : null,
    timezone: g.timezone || null,
  };
}

export function VenueTbaField({ id, inputProps }: AutoFormFieldProps) {
  const form = useFormContext();
  const checked = !!useWatch({ name: id });
  const geodata = useWatch({ name: "geodata" });
  const formGeolocation = useWatch({ name: "geolocation" });
  const center = readCityCenter(geodata);
  const geoId: string | undefined = geodata?.id;

  const lat = center.geolocation?.latitude;
  const lng = center.geolocation?.longitude;
  const timezone = center.timezone;
  const previous = React.useRef<{ checked: boolean; geoId?: string } | null>(
    null,
  );

  // While checked, the city's centre point and timezone stand in for a venue's, so they
  // follow the city. A city without a centre point clears the pin instead: the previous
  // venue's coordinates must never survive as the TBA location, so validation fails until
  // the curator types one. The first run is a load sync, not a curator edit: it never
  // marks the form dirty and never clears (a Tina-made TBA event keeps its own centre).
  React.useEffect(() => {
    const prev = previous.current;
    previous.current = { checked, geoId };
    if (!checked) return;
    const isLoad = prev === null;
    const changed = isLoad || !prev.checked || prev.geoId !== geoId;
    if (!changed) return;
    const opts = { shouldDirty: !isLoad };

    if (lat !== undefined && lng !== undefined) {
      const current = form.getValues("geolocation");
      if (current?.latitude !== lat || current?.longitude !== lng) {
        form.setValue("geolocation", { latitude: lat, longitude: lng }, opts);
      }
    } else if (!isLoad) {
      form.setValue(
        "geolocation",
        { latitude: undefined, longitude: undefined },
        opts,
      );
    }
    if (timezone && form.getValues("timezone") !== timezone) {
      form.setValue("timezone", timezone, opts);
    }
  }, [checked, geoId, lat, lng, timezone, form]);

  const handleChange = (next: boolean) => {
    if (next) {
      form.setValue("venue", null, { shouldDirty: true });
      form.setValue("mapsUrl", null, { shouldDirty: true });
    }
    // react-hook-form expects an event object
    inputProps.onChange({ target: { name: id, value: next } });
  };

  return (
    <div className="space-y-1">
      <div className="flex items-center space-x-2">
        <Checkbox id={id} checked={checked} onCheckedChange={handleChange} />
        <Label htmlFor={id}>Venue TBA</Label>
      </div>
      {checked && !center.geolocation && !hasCityCentre(formGeolocation) && (
        <p className="text-sm text-muted-foreground">
          This city has no center point — enter latitude and longitude.
        </p>
      )}
    </div>
  );
}
